import { IconButton } from "@dropbox/dig-components/buttons";
import { Split, Stack } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import {
  CopyLine,
  ThumbsDownFill,
  ThumbsDownLine,
  ThumbsUpFill,
  ThumbsUpLine,
} from "@dropbox/dig-icons/assets";
import { useChatTrialUpsell } from "@mirage/mosaics/TrialEndingNotification";
import { FeedbackOptions } from "@mirage/shared/types";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import i18n from "@mirage/translations";
import classNames from "classnames";
import { createRef, memo, useCallback, useState } from "react";
import styles from "./ConversationMessageActions.module.css";
import type { LayoutVariant } from "./ConversationMessages/types";

interface ConversationMessageActionsProps {
  variant: LayoutVariant;
  onClickFeedback: (feedback: FeedbackOptions) => void;
  onCopyMessage?: () => void;
}
export const ConversationMessageActions = memo(
  ({
    variant,
    onCopyMessage,
    onClickFeedback,
  }: ConversationMessageActionsProps) => {
    const [recordedFeedback, setRecordedFeedback] = useState<
      FeedbackOptions | undefined
    >();
    const [showCopy, setShowCopy] = useState(false);
    const [showCopied, setShowCopied] = useState(false);
    const copyBtnRef = createRef<HTMLButtonElement>();
    const { chatCopiedTriggerUpsellCheck } = useChatTrialUpsell();
    const handleClickFeedback = (feedback: FeedbackOptions) => {
      if (recordedFeedback === feedback) {
        setRecordedFeedback(undefined);
      } else {
        setRecordedFeedback(feedback);
        onClickFeedback(feedback);
      }
    };
    const handleCopy = useCallback(() => {
      if (onCopyMessage) {
        onCopyMessage();
        chatCopiedTriggerUpsellCheck();
        setShowCopied(true);
        setTimeout(() => setShowCopied(false), 2000);
      }
    }, [onCopyMessage, chatCopiedTriggerUpsellCheck]);
    const positiveIcon =
      recordedFeedback === FeedbackOptions.Positive
        ? ThumbsUpFill
        : ThumbsUpLine;
    const negativeIcon =
      recordedFeedback === FeedbackOptions.Negative
        ? ThumbsDownFill
        : ThumbsDownLine;
    return (
      <Stack
        className={classNames(styles.ConversationMessageActionsContainer, {
          [styles.ConversationMessageActionsContainerCondensed]:
            variant === "condensed",
        })}
      >
        <Split>
          <Split.Item>
            <DigTooltip title={i18n.t("helpful")}>
              <IconButton
                size="small"
                variant="borderless"
                onClick={() => handleClickFeedback(FeedbackOptions.Positive)}
                aria-label={i18n.t("helpful")}
                aria-pressed={recordedFeedback === FeedbackOptions.Positive}
              >
                <UIIcon
                  className={styles.ConversationMessageActionsButton}
                  src={positiveIcon}
                />
              </IconButton>
            </DigTooltip>
            <DigTooltip title={i18n.t("not_helpful")}>
              <IconButton
                size="small"
                variant="borderless"
                onClick={() => handleClickFeedback(FeedbackOptions.Negative)}
                aria-label={i18n.t("not_helpful")}
                aria-pressed={recordedFeedback === FeedbackOptions.Negative}
              >
                <UIIcon
                  className={styles.ConversationMessageActionsButton}
                  src={negativeIcon}
                />
              </IconButton>
            </DigTooltip>
          </Split.Item>
          {onCopyMessage && (
            <Split.Item>
              <IconButton
                ref={copyBtnRef}
                size="small"
                variant="borderless"
                onClick={handleCopy}
                aria-label={i18n.t("copy")}
                onMouseEnter={() => setShowCopy(true)}
                onMouseLeave={() => setShowCopy(false)}
              >
                <UIIcon
                  className={styles.ConversationMessageActionsButton}
                  src={CopyLine}
                />
              </IconButton>
            </Split.Item>
          )}
          <DigTooltip.Control
            triggerRef={copyBtnRef}
            placement="top"
            open={showCopied || showCopy}
            shouldReturnFocus={false}
            autoHide
            openDelay={250}
          >
            {showCopied ? i18n.t("copied") : i18n.t("copy")}
          </DigTooltip.Control>
        </Split>
      </Stack>
    );
  },
);
ConversationMessageActions.displayName = "ConversationMessageActions";
